import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import { CustodianItem } from '@/types';
import { Spinner } from '@/components/ui/Spinner';
import { ColumnHeader } from '@/features/inventory/components/helpers/ColumnHeader';

interface CustodianTableProps {
  data: CustodianItem[];
  columns: ColumnDef<CustodianItem>[];
  isLoading?: boolean;
  /**
   * LR-625: the read failed AND nothing is loaded — a failed refresh over cached
   * rows must not reach here, or the branch below blanks a populated table. The
   * caller decides that from the unfiltered list.
   */
  isError?: boolean;
}

// LR-441: mirrors CounterpartyTable (fixed-width columns + ColumnHeader), scoped
// to the custodian register. Sorting is not wired yet (parity with CP table).
export const CustodianTable: React.FC<CustodianTableProps> = ({ data, columns, isLoading, isError }) => {
  const processedData = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const table = useReactTable({
    data: processedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return <Spinner message="Loading custodians..." />;
  }

  // LR-625: this is the page the Trade Entry hint sends the trader to. Rendering
  // the affirmative "No custodians found." over a failed read would tell them to
  // create a custodian that may already exist (LR-572).
  if (isError) {
    return (
      <div
        role="alert"
        className="flex flex-1 items-center justify-center p-10 text-sm text-red-700"
      >
        Couldn&apos;t load custodians — this is a failed request, not an empty register.
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-auto min-h-0">
      <div className="flex flex-1 flex-col" style={{ minWidth: 'max-content' }}>
        <div className="sticky top-0 z-10 flex items-center border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
          {columns.map((column: { accessorKey?: string; id?: string; size?: number; header?: unknown }, colIndex: number) => {
            const columnId = column.accessorKey || column.id || '';
            const width = column.size || 150;
            const headerText = typeof column.header === 'string' ? column.header : '';

            return (
              <div
                key={colIndex}
                className="mx-1.5 flex min-w-0"
                style={{ width: `${width}px`, minWidth: `${width}px` }}
              >
                <ColumnHeader
                  columnId={columnId}
                  headerText={headerText}
                  width={width}
                  sortState={undefined}
                  onSort={() => {}}
                  isLoading={!!isLoading}
                  hasError={false}
                  hoveredColumn={null}
                  onHover={() => {}}
                  align="left"
                />
              </div>
            );
          })}
        </div>

        <div className="flex-1">
          {table.getRowModel().rows.map((row) => (
            <div key={row.id} className="flex items-center border-b border-gray-200 px-4 py-2 hover:bg-gray-50 transition-colors">
              {row.getVisibleCells().map((cell) => {
                const width = cell.column.columnDef.size || 150;
                return (
                  <div
                    key={cell.id}
                    className="mx-1.5 text-sm text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap"
                    style={{ width: `${width}px`, minWidth: `${width}px` }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                );
              })}
            </div>
          ))}
          {processedData.length === 0 && (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              No custodians found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
